import React from "react";
import { Theme, alpha } from "@mui/material/styles";
import { Box, Checkbox, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { makeStyles } from "tss-react/mui";
import { MAIN_SITE } from "../websites/domains";

// Building blocks of the step-by-step guides: numbered steps with a check box, copyable commands, hints

export type RunWhere = "vm63" | "pc" | "plesk" | "azure" | "adino" | "winscp" | "browser" | "ssh" | "storage" | "b2c";

const WHERE: Record<RunWhere, { label: string; color: string }> = {
  vm63: { label: "SSH on VM63", color: "#2e7d32" },
  pc: { label: "Windows PC · PowerShell / cmd", color: "#0277bd" },
  plesk: { label: "Plesk (web)", color: "#ef6c00" },
  azure: { label: "Azure Portal", color: "#0078d4" },
  adino: { label: "Adino nsentry (web)", color: "#1565c0" },
  winscp: { label: "WinSCP editor", color: "#6d4c41" },
  browser: { label: "Browser", color: "#546e7a" },
  ssh: { label: "SSH on the server", color: "#2e7d32" },
  storage: { label: "Azure Storage Explorer", color: "#0063b1" },
  b2c: { label: "Azure AD B2C", color: "#5c2d91" },
};

const useStyles = makeStyles()((theme: Theme) => ({
  step: {
    display: "grid",
    gridTemplateColumns: "36px 1fr",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px dashed ${theme.palette.divider}`,
    "&:last-of-type": { borderBottom: "none" },
  },
  number: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    fontSize: 14,
    color: "#fff",
    background: theme.palette.primary.main,
    transition: "background .2s",
  },
  numberDone: { background: "#43a047" },
  stepTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: 15,
    minHeight: 32,
  },
  done: { opacity: 0.55 },
  where: {
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 10,
    color: "#fff",
    whiteSpace: "nowrap",
  },
  body: { fontSize: 13.5, lineHeight: 1.55, "& p": { margin: "4px 0" }, "& ul": { margin: "4px 0", paddingLeft: 20 } },
  code: {
    position: "relative",
    // Long command lines scroll inside the block instead of widening the page
    contain: "inline-size",
    margin: theme.spacing(1, 0),
    borderRadius: 8,
    background: "#1e2430",
    color: "#e6edf3",
    "& pre": {
      margin: 0,
      padding: theme.spacing(1.5, 6, 1.5, 1.75),
      overflowX: "auto",
      fontFamily: "'JetBrains Mono', 'Consolas', monospace",
      fontSize: 12.5,
      lineHeight: 1.55,
    },
  },
  codeLabel: {
    padding: theme.spacing(0.5, 1.75),
    fontSize: 11,
    color: "#9fb0c3",
    borderBottom: "1px solid #2f3747",
  },
  copy: { position: "absolute", top: 6, right: 6, color: "#9fb0c3", "&:hover": { color: "#fff" } },
  hint: {
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    fontSize: 13,
    lineHeight: 1.5,
    borderLeft: "4px solid",
  },
}));

export const CodeBlock: React.FunctionComponent<{ code: string; label?: string }> = ({ code, label }) => {
  const { classes } = useStyles();
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className={classes.code}>
      {label && <div className={classes.codeLabel}>{label}</div>}
      <Tooltip title={copied ? "Copied" : "Copy"}>
        <IconButton size="small" className={classes.copy} onClick={copy} style={label ? { top: 30 } : undefined}>
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <pre>{code}</pre>
    </div>
  );
};

const HINT_COLORS = { info: "#0277bd", warn: "#e5ac58", danger: "#e53935", ok: "#43a047" };

export const Hint: React.FunctionComponent<{ type?: keyof typeof HINT_COLORS; children: React.ReactNode }> = ({ type = "info", children }) => {
  const { classes } = useStyles();
  const color = HINT_COLORS[type];
  return (
    <div className={classes.hint} style={{ borderLeftColor: color, background: alpha(color, 0.1) }}>
      {children}
    </div>
  );
};

/** Expected result of a command */
export const Expect: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => (
  <Hint type="ok">
    <b>You should see: </b>
    {children}
  </Hint>
);

export const Step: React.FunctionComponent<{
  n: number;
  title: string;
  where?: RunWhere;
  done: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}> = ({ n, title, where, done, onToggle, children }) => {
  const { classes, cx } = useStyles();
  return (
    <div className={classes.step}>
      <div className={cx(classes.number, { [classes.numberDone]: done })}>{done ? <CheckIcon fontSize="small" /> : n}</div>
      <div>
        <div className={cx(classes.stepTitle, { [classes.done]: done })}>
          <span style={{ flex: 1 }}>{title}</span>
          {where && (
            <span className={classes.where} style={{ background: WHERE[where].color }}>
              {WHERE[where].label}
            </span>
          )}
          <Tooltip title={done ? "Mark as not done" : "Mark as done"}>
            <Checkbox size="small" checked={done} onChange={onToggle} sx={{ p: 0.5 }} />
          </Tooltip>
        </div>
        <Box className={cx(classes.body, { [classes.done]: done })}>{children}</Box>
      </div>
    </div>
  );
};

/** Check box state of the steps of one guide; reset when the inputs change */
export const useSteps = (resetKey: string) => {
  const [done, setDone] = React.useState<Set<number>>(new Set());
  React.useEffect(() => setDone(new Set()), [resetKey]);
  const toggle = (n: number) =>
    setDone((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  return { isDone: (n: number) => done.has(n), toggle: (n: number) => () => toggle(n), count: done.size };
};

/** "https://www.Asr.ch/x" → "asr.ch" */
export const cleanDomain = (input: string) =>
  input
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/^www\./, "");

export const isDomain = (d: string) => /^(?!-)[a-z0-9-]{1,63}(\.[a-z0-9-]{1,63})*\.[a-z]{2,}$/.test(d);

/** Full redirect URL from a path or a URL; paths always point to loacker-recycling.com */
export const targetUrl = (input: string) => {
  const value = input.trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const path = `/${value.replace(/^\/+/, "")}`;
  return `${MAIN_SITE}${path.endsWith("/") || path.includes("?") ? path : `${path}/`}`;
};

/** Old URL path on the redirected domain, e.g. "de/kontakt" → "/de/kontakt/" */
export const oldPath = (input: string) => {
  const value = input.trim().replace(/^[a-z]+:\/\/[^/]+/i, "");
  if (!value) return "";
  return `/${value.replace(/^\/+/, "")}`;
};
